import React, { useState, useEffect } from "react";
import { FileDown, ChevronDown, ChevronUp, Star } from "lucide-react";

// Interface disesuaikan langsung dengan field yang dikembalikan backend
interface RekapStatusItem {
  id_karyawan: string;
  nama: string;
  uraianKelasJabatan: string;
  hadir: number;
  izin: number;
  sakit: number;
  dinas_luar: number;
  cuti : number;
  alfa: number;
  jumlah_hari: number;
}

export default function DashboardRekapStatus() {
  const [periode, setPeriode] = useState(() => {
    const tanggal = new Date();
    const tahun = tanggal.getFullYear();
    const bulan = String(tanggal.getMonth() + 1).padStart(2, "0"); 
    const hari = String(tanggal.getDate()).padStart(2, "0");
    return `${tahun}-${bulan}-${hari}`;
  });
  const [employees, setEmployees] = useState<RekapStatusItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/bawaslu_phwt/rekapan/absensi?tanggal=${periode}`)
      .then((res) => res.json())
      .then((result) => {
        const rawData = Array.isArray(result) ? result : result.data || [];
        setEmployees(rawData);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Gagal memuat data rekap status:", error);
        setLoading(false);
      });
  }, [periode]);

  const exportXlsx = async () => {
    const XLSX = await import("xlsx");

    const header = [
      "No.", "Nama", "Jabatan", "Hadir", "Izin", "Sakit", "Dinas Luar", "Cuti", "Alfa", "Jumlah Hari",
    ];

    const data = employees.map((e, i) => [
      i + 1,
      e.nama,
      e.uraianKelasJabatan,
      e.hadir,
      e.izin,
      e.sakit,
      e.dinas_luar,
      e.cuti,
      e.alfa,
      e.jumlah_hari,
    ]);

    const aoa = [[`Rekapitulasi Status Pegawai - ${periode}`], [], header, ...data];
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: header.length - 1 } },
    ];

    ws["!cols"] = header.map((h) => ({ wch: Math.max(8, Math.min(30, h.length + 6)) }));

    ws["!freeze"] = { xSplit: 0, ySplit: 3 };

    for (let r = 3; r < aoa.length; r++) {
      const row = aoa[r];
      if (!row) continue;

      for (let c = 3; c <= 9; c++) {
        const cellAddress = XLSX.utils.encode_cell({ r: r, c });
        const cell = ws[cellAddress];
        if (cell && (cell.v === null || cell.v === undefined || cell.v === "")) continue;
        if (cell) cell.t = "n";
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Rekap Status");
    XLSX.writeFile(wb, `Rekap_Status_${periode}.xlsx`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
      <div className="max-w-8xl mx-auto">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center">
            <Star className="w-8 h-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-800">Rekapan Pemotongan Tunjangan</h1>
          </div>

          <div className="flex gap-2">
            <input 
              type="date" 
              value={periode} 
              onChange={(e) => setPeriode(e.target.value)}
              className="border border-slate-300 rounded-xl px-3 py-1.5 text-xs bg-white"
            />
            {/* <button
              onClick={() => setShowDetail((s) => !s)}
              className="flex items-center gap-1 rounded-xl border border-slate-200 bg-slate-100 px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-200"
            >
              {showDetail ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {showDetail ? "Sembunyikan Detail" : "Tampilkan Detail"}
            </button> */}
            <button
              onClick={exportXlsx}
              className="flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-yellow-400 to-amber-300 px-4 py-2 text-xs font-semibold text-slate-900 hover:from-yellow-300"
            >
              <FileDown size={14} />
              Ekspor Excel
            </button>
          </div>
        </div>

        <div className="relative mx-auto max-w-full rounded-lg border border-slate-200 bg-white shadow-sm overflow-x-auto">
          <table className="w-max min-w-full border-collapse text-[12px]">
            <thead>
              <tr className="bg-[#152544] text-white text-center">
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>No.</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Nama</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Jabatan</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Hadir</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Izin</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Sakit</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Dinas Luar</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Cuti</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Alfa</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Jumlah Hari</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={13} className="py-8 text-center text-slate-500 italic">
                    Memuat data rekap pemotongan...
                  </td>
                </tr>
              ) : employees.length === 0 ? (
                <tr>
                  <td colSpan={13} className="py-8 text-center text-slate-500 italic">
                    Tidak ada data pegawai yang tersedia.
                  </td>
                </tr>
              ) : (
                employees.map((e, i) => (
                  <tr key={e.id_karyawan || i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                    <td className="border border-slate-200 px-2 py-1 text-center">{i + 1}</td>
                    <td className="border border-slate-200 px-2 py-1 font-medium">{e.nama || "0"}</td>
                    <td className="border border-slate-200 px-2 py-1">{e.uraianKelasJabatan || "0"}</td>
                    <td className="border border-slate-200 px-2 py-1">{e.hadir || "0"}</td>
                    <td className="border border-slate-200 px-2 py-1">{e.izin || "0"}</td>
                    <td className="border border-slate-200 px-2 py-1">{e.sakit || "0"}</td>
                    <td className="border border-slate-200 px-2 py-1">{e.dinas_luar || "0"}</td>
                    <td className="border border-slate-200 px-2 py-1">{e.cuti || "0"}</td>
                    <td className="border border-slate-200 px-2 py-1">{e.alfa || "0"}</td>
                    <td className="border border-slate-200 px-2 py-1">{e.jumlah_hari || "0"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}